import React, { useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { DealDetail } from '../../services/dealService';
import dealService from '../../services/dealService';
import axiosInstance from '../../helpers/api';

interface DealMapProps {
  deal: DealDetail;
  compDetails: any[];
  isVisible?: boolean;
}

const DealMap: React.FC<DealMapProps> = ({ deal, compDetails, isVisible = true }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapError, setMapError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    console.log('🗺️ DealMap useEffect triggered, isVisible:', isVisible);
    console.log('Deal coordinates:', { lat: deal.latitude, lng: deal.longitude });
    console.log('CompDetails:', compDetails);
    
    // Only initialize map when visible
    if (!isVisible) {
      console.log('⏸️ Tab not visible, skipping map initialization');
      return;
    }
    
    // Skip if map already exists
    if (map.current) {
      console.log('✅ Map already initialized');
      return;
    }
    
    if (!mapContainer.current) {
      console.error('❌ Map container not ready');
      return;
    }
    
    if (!deal.latitude || !deal.longitude) {
      console.error('❌ Deal coordinates missing');
      return;
    }

    // Initialize map
    const initializeMap = async () => {
      try {
        // Get Mapbox token from API
        console.log('🗺️ Fetching Mapbox token...');
        const response = await axiosInstance.get('/properties/config/mapbox');
        const data = response.data;
        
        if (!data.mapboxToken) {
          console.error('❌ No Mapbox token available');
          return;
        }

        console.log('✅ Got Mapbox token, initializing map...');
        mapboxgl.accessToken = data.mapboxToken;

        map.current = new mapboxgl.Map({
          container: mapContainer.current!,
          style: 'mapbox://styles/mapbox/satellite-streets-v12',
          center: [deal.longitude, deal.latitude],
          zoom: 15,
          pitch: 45,
          bearing: -17.6
        });
        
        // Wait for map to load
        map.current.on('load', () => {
          console.log('✅ Map loaded successfully');
          setIsLoading(false);
          
          // Ensure proper sizing after style loads
          setTimeout(() => {
            if (map.current) {
              map.current.resize();
              console.log('✅ Map resized after load');
            }
          }, 300);
        });

        // Add navigation controls
        map.current.addControl(new mapboxgl.NavigationControl());
        map.current.addControl(new mapboxgl.FullscreenControl());
        map.current.addControl(new mapboxgl.ScaleControl());

        // Add subject property marker
        new mapboxgl.Marker({
          color: '#dc3545' // Red for subject property
        })
          .setLngLat([deal.longitude, deal.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25 }).setHTML(`
              <div style="min-width: 200px;">
                <h6 class="mb-2 text-danger">📍 Subject Property</h6>
                <strong>${deal.address || deal.unparsed_address}</strong>
                <hr class="my-2">
                <div class="small">
                  <div class="d-flex justify-content-between mb-1">
                    <span>List Price:</span>
                    <strong>${dealService.formatCurrency(deal.list_price)}</strong>
                  </div>
                  <div class="d-flex justify-content-between mb-1">
                    <span>Flip Grade:</span>
                    <span class="badge bg-${deal.flip_grade === 'A+' ? 'danger' : deal.flip_grade === 'A' ? 'warning' : 'secondary'}">${deal.flip_grade}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-1">
                    <span>ROI:</span>
                    <strong>${dealService.formatROI(deal.flip_roi)}</strong>
                  </div>
                  <div class="d-flex justify-content-between">
                    <span>Profit:</span>
                    <strong class="text-success">${dealService.formatCurrency(deal.flip_profit)}</strong>
                  </div>
                </div>
              </div>
            `)
          )
          .addTo(map.current);

        // Add comp markers from parsed comp data
        const topRenovatedComps = deal.top_renovated_comps ? 
          (typeof deal.top_renovated_comps === 'string' ? 
            JSON.parse(deal.top_renovated_comps) : deal.top_renovated_comps) : [];
        
        const topAsIsComps = deal.top_as_is_comps ? 
          (typeof deal.top_as_is_comps === 'string' ? 
            JSON.parse(deal.top_as_is_comps) : deal.top_as_is_comps) : [];
        
        // Create a map to store coordinates by listing_id
        const compCoordinates: Record<string, {latitude: number, longitude: number}> = {};
        
        // First, collect any coordinates from compDetails (these would have been updated via MLS fetch)
        if (compDetails && compDetails.length > 0) {
          compDetails.forEach(comp => {
            if (comp.listing_id && comp.latitude && comp.longitude) {
              compCoordinates[comp.listing_id] = {
                latitude: comp.latitude,
                longitude: comp.longitude
              };
            }
          });
        }
        
        console.log('📍 Comp coordinates available:', compCoordinates);
        
        // Add renovated comps
        topRenovatedComps.forEach((comp: any) => {
          // Check for coordinates in the comp object or from our coordinate map
          const coords = compCoordinates[comp.listing_id] || 
                        (comp.latitude && comp.longitude ? { latitude: comp.latitude, longitude: comp.longitude } : null);
          
          if (coords) {
            new mapboxgl.Marker({ color: '#28a745' })
              .setLngLat([coords.longitude, coords.latitude])
              .setPopup(
                new mapboxgl.Popup({ offset: 25 }).setHTML(`
                  <div style="min-width: 200px;">
                    <h6 class="mb-2 text-success">🏡 Renovated Comp</h6>
                    <strong>${comp.address}</strong>
                    <hr class="my-2">
                    <div class="small">
                      <div class="d-flex justify-content-between mb-1">
                        <span>Sold Price:</span>
                        <strong>${dealService.formatCurrency(comp.close_price)}</strong>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span>$/SqFt:</span>
                        <span>$${Math.round(comp.price_per_sqft)}</span>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span>Distance:</span>
                        <span>${comp.distance_miles} mi</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Sold Date:</span>
                        <span>${new Date(comp.close_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                `)
              )
              .addTo(map.current!);
          }
        });
        
        // Add as-is comps
        topAsIsComps.forEach((comp: any) => {
          // Check for coordinates in the comp object or from our coordinate map
          const coords = compCoordinates[comp.listing_id] || 
                        (comp.latitude && comp.longitude ? { latitude: comp.latitude, longitude: comp.longitude } : null);
          
          if (coords) {
            new mapboxgl.Marker({ color: '#007bff' })
              .setLngLat([coords.longitude, coords.latitude])
              .setPopup(
                new mapboxgl.Popup({ offset: 25 }).setHTML(`
                  <div style="min-width: 200px;">
                    <h6 class="mb-2 text-primary">🏠 As-Is Comp</h6>
                    <strong>${comp.address}</strong>
                    <hr class="my-2">
                    <div class="small">
                      <div class="d-flex justify-content-between mb-1">
                        <span>Sold Price:</span>
                        <strong>${dealService.formatCurrency(comp.close_price)}</strong>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span>$/SqFt:</span>
                        <span>$${Math.round(comp.price_per_sqft)}</span>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span>Distance:</span>
                        <span>${comp.distance_miles} mi</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Sold Date:</span>
                        <span>${new Date(comp.close_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                `)
              )
              .addTo(map.current!);
          }
        });
        
        // Also add any comps from compDetails if they exist
        compDetails.forEach(comp => {
          if (comp.latitude && comp.longitude) {
            const color = comp.classification === 'renovated' ? '#28a745' : '#007bff';
            
            new mapboxgl.Marker({ color })
              .setLngLat([comp.longitude, comp.latitude])
              .setPopup(
                new mapboxgl.Popup({ offset: 25 }).setHTML(`
                  <div style="min-width: 200px;">
                    <h6 class="mb-2 text-${comp.classification === 'renovated' ? 'success' : 'primary'}">
                      ${comp.classification === 'renovated' ? '🏡 Renovated Comp' : '🏠 As-Is Comp'}
                    </h6>
                    <strong>${comp.comp_address || comp.address}</strong>
                    <hr class="my-2">
                    <div class="small">
                      <div class="d-flex justify-content-between mb-1">
                        <span>Sold Price:</span>
                        <strong>${dealService.formatCurrency(comp.comp_close_price || comp.close_price)}</strong>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span>$/SqFt:</span>
                        <span>$${Math.round(comp.comp_price_per_sqft || comp.price_per_sqft)}</span>
                      </div>
                      <div class="d-flex justify-content-between mb-1">
                        <span>Distance:</span>
                        <span>${comp.comp_distance_miles || comp.distance_miles} mi</span>
                      </div>
                      <div class="d-flex justify-content-between">
                        <span>Sold Date:</span>
                        <span>${new Date(comp.comp_close_date || comp.close_date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                `)
              )
              .addTo(map.current!);
          }
        });

        // Fit bounds to show all markers
        const bounds = new mapboxgl.LngLatBounds();
        bounds.extend([deal.longitude, deal.latitude]);
        
        // Include all comps in bounds
        topRenovatedComps.forEach((comp: any) => {
          if (comp.latitude && comp.longitude) {
            bounds.extend([comp.longitude, comp.latitude]);
          }
        });
        
        topAsIsComps.forEach((comp: any) => {
          if (comp.latitude && comp.longitude) {
            bounds.extend([comp.longitude, comp.latitude]);
          }
        });
        
        compDetails.forEach(comp => {
          if (comp.latitude && comp.longitude) {
            bounds.extend([comp.longitude, comp.latitude]);
          }
        });

        map.current.fitBounds(bounds, { 
          padding: { top: 50, bottom: 50, left: 50, right: 50 },
          maxZoom: 16
        });

      } catch (error: any) {
        console.error('❌ Failed to initialize map:', error);
        setMapError(error.message || 'Failed to load map');
        setIsLoading(false);
      }
    };

    initializeMap();

    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [deal, compDetails, deal.top_renovated_comps, deal.top_as_is_comps, isVisible]);


  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Property & Comps Map</h5>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle me-2" 
                style={{ width: '12px', height: '12px', backgroundColor: '#dc3545' }}
              />
              <small>Subject Property</small>
            </div>
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle me-2" 
                style={{ width: '12px', height: '12px', backgroundColor: '#28a745' }}
              />
              <small>Renovated Comps</small>
            </div>
            <div className="d-flex align-items-center">
              <div 
                className="rounded-circle me-2" 
                style={{ width: '12px', height: '12px', backgroundColor: '#007bff' }}
              />
              <small>As-Is Comps</small>
            </div>
          </div>
        </div>
        {isLoading && (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '800px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading map...</span>
            </div>
          </div>
        )}
        
        {mapError && (
          <div className="alert alert-warning" role="alert">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Unable to load map: {mapError}
          </div>
        )}
        
        <div 
          ref={mapContainer} 
          style={{ 
            height: '800px', 
            width: '100%', 
            borderRadius: '8px',
            border: '1px solid #dee2e6',
            display: isLoading || mapError ? 'none' : 'block'
          }}
        />
      </Card.Body>
    </Card>
  );
};

export default DealMap;